import React, { useState, useEffect, Fragment } from 'react';
import { NT, NI, NMButton, NMChip } from './NaomiTheme';
import { MDialog, MSelect, MTextField } from './NaomiUi';

const WarnIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M8 2L14 13H2L8 2z"/><line x1="8" y1="9" x2="8" y2="11"/><line x1="8" y1="6" x2="8" y2="7"/>
  </svg>
);

function ChoiceWarningEditor({ choices, warnings, onChange }: {
  choices: Array<{ CodedValue: string; label: string }>;
  warnings: Record<string, string>;
  onChange: (w: Record<string, string>) => void;
}) {
  const [openCvs, setOpenCvs] = useState<string[]>(() =>
    choices.filter(c => warnings[c.CodedValue]).map(c => c.CodedValue)
  );
  const [pendingCv, setPendingCv] = useState('');

  const openChoices = choices.filter(c => openCvs.includes(c.CodedValue));
  const remaining   = choices.filter(c => !openCvs.includes(c.CodedValue));

  const handleAdd = () => {
    if (!pendingCv) return;
    setOpenCvs(prev => [...prev, pendingCv]);
    setPendingCv('');
  };

  const handleRemove = (cv: string) => {
    setOpenCvs(prev => prev.filter(x => x !== cv));
    const next = { ...warnings };
    delete next[cv];
    onChange(next);
  };

  const handleMsg = (cv: string, msg: string) => {
    onChange({ ...warnings, [cv]: msg });
  };

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {openChoices.length === 0 && (
        <div style={{ fontSize: 11.5, color: NT.ink4, fontStyle: 'italic' }}>
          No warnings configured — use the picker below to add one.
        </div>
      )}
      {openChoices.map(c => (
        <div key={c.CodedValue} style={{ display: 'grid', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: NT.warn, background: NT.warnSoft,
              padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
              maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis',
            }} title={c.label}>{c.label}</span>
            <button
              onClick={() => handleRemove(c.CodedValue)}
              title="Remove warning"
              style={{
                width: 22, height: 22, border: 'none', background: 'transparent',
                cursor: 'pointer', color: NT.ink3, borderRadius: 4,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = NT.danger; (e.currentTarget as HTMLButtonElement).style.background = NT.dangerSoft; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = NT.ink3; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >×</button>
          </div>
          <input
            value={warnings[c.CodedValue] || ''}
            onChange={e => handleMsg(c.CodedValue, e.target.value)}
            placeholder="Warning message…"
            autoFocus={!warnings[c.CodedValue]}
            style={{
              width: '100%', height: 36, padding: '0 12px', boxSizing: 'border-box',
              border: `1px solid ${NT.warn}66`, borderRadius: 6,
              fontSize: 13, fontFamily: 'inherit', color: NT.ink,
              outline: 'none', background: NT.warnSoft + '40',
            }}
          />
        </div>
      ))}
      {remaining.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: openChoices.length > 0 ? 2 : 0 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <select
              value={pendingCv}
              onChange={e => setPendingCv(e.target.value)}
              style={{
                width: '100%', height: 30, padding: '0 26px 0 9px', appearance: 'none',
                border: `1px solid ${NT.line2}`, borderRadius: 6, cursor: 'pointer',
                background: NT.surface, color: pendingCv ? NT.ink : NT.ink4,
                fontSize: 12.5, fontFamily: 'inherit',
              }}
            >
              <option value="">— Add warning for… —</option>
              {remaining.map(c => <option key={c.CodedValue} value={c.CodedValue}>{c.label}</option>)}
            </select>
            <div style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: NT.ink3 }}>
              <NI.Chev/>
            </div>
          </div>
          <button
            id="naomi-fieldtype-button-add-choice-warning"
            disabled={!pendingCv}
            onClick={handleAdd}
            style={{
              height: 30, padding: '0 10px', borderRadius: 6, flexShrink: 0,
              border: `1px solid ${pendingCv ? NT.warn : NT.line2}`,
              background: pendingCv ? NT.warnSoft : NT.surfaceAlt,
              color: pendingCv ? NT.warn : NT.ink4,
              fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
              cursor: pendingCv ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            + Add
          </button>
        </div>
      )}
      <div style={{ fontSize: 11, color: NT.ink4, lineHeight: 1.4 }}>
        Soft — lets the user proceed after acknowledging. Use for cautions, not blocking rules.
      </div>
    </div>
  );
}

export const DataTypeOptions2 = [
  { value: 'text',         label: 'Short text',     icon: <NI.Text/>,   group: 'Text' },
  { value: 'multilinetext', label: 'Long text',      icon: <NI.Text/>,   group: 'Text' },
  { value: 'integer',      label: 'Whole number',   icon: <NI.Number/>, group: 'Number' },
  { value: 'float',        label: 'Decimal',        icon: <NI.Number/>, group: 'Number' },
  { value: 'currency',     label: 'Currency ($)',   icon: <NI.Money/>,  group: 'Number' },
  { value: 'percent',      label: 'Percent (%)',    icon: <NI.Percent/>,group: 'Number' },
  { value: 'feetinches',   label: 'Feet + Inches',  icon: <NI.Ruler/>,  group: 'Measurement' },
  { value: 'boolean',      label: 'Boolean (Yes/No)', icon: <NI.Toggle/>, group: 'Choice' },
  { value: 'singleselect', label: 'Single choice',  icon: <NI.Radio/>,  group: 'Choice' },
  { value: 'multiselect',  label: 'Multi choice',   icon: <NI.Check2/>, group: 'Choice' },
  { value: 'date',         label: 'Date',           icon: <NI.Cal/>,    group: 'Date & time' },
  { value: 'time',         label: 'Time',           icon: <NI.Clock/>,  group: 'Date & time' },
  { value: 'datetime',     label: 'Date + Time',    icon: <NI.Cal/>,    group: 'Date & time' },
];

export const TypeMeta2 = Object.fromEntries(DataTypeOptions2.map(o => [o.value, o]));

Object.assign(window.TypeMeta || {}, Object.fromEntries(
  DataTypeOptions2.map(o => [o.value, { icon: o.icon, label: o.label }])
));
window.DataTypeOptions = DataTypeOptions2;
window.TypeMeta = { ...(window.TypeMeta || {}), ...TypeMeta2 };

export function unitsFor(dataType, allUnits) {
  if (dataType === 'integer' || dataType === 'float') {
    return allUnits.filter(u => !u.builtin || ['MU.1','MU.2','MU.3','MU.4','MU.5'].includes(u.OID) || u.userAdded);
  }
  if (dataType === 'currency') return [{ OID: 'MU.USD', Name: 'US Dollar', Symbol: '$', builtin: true }];
  if (dataType === 'percent')  return [{ OID: 'MU.PCT', Name: 'Percent',   Symbol: '%', builtin: true }];
  return [];
}

const DISABLED_DATA_TYPES = new Set(['currency', 'percent', 'datetime', 'time', 'image', 'composite']);

export function DataTypePicker({ value, onChange, label = 'Answer type' }) {
  const groups = Array.from(new Set(DataTypeOptions2.map(o => o.group))).filter(Boolean);
  return (
    <div>
      {label && <div style={{ fontSize: 11.5, fontWeight: 600, color: NT.ink2, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 }}>{label}</div>}
      <div style={{ position: 'relative' }}>
        <select value={value} onChange={e => onChange(e.target.value)}
          style={{ width: '100%', height: 40, padding: '0 32px 0 10px', borderRadius: 6,
            border: `1px solid ${NT.line2}`, background: NT.surface, color: NT.ink, fontSize: 13.5,
            fontFamily: 'inherit', appearance: 'none', cursor: 'pointer' }}>
          {groups.map(g => (
            <optgroup key={g} label={g}>
              {DataTypeOptions2.filter(o => o.group === g).map(o => {
                const dis = DISABLED_DATA_TYPES.has(o.value);
                return (
                  <option key={o.value} value={o.value} disabled={dis}>
                    {o.label}{dis ? ' — not supported' : ''}
                  </option>
                );
              })}
            </optgroup>
          ))}
        </select>
        <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: NT.ink3 }}>
          <NI.Chev/>
        </div>
      </div>
    </div>
  );
}

export function ValidationEditor({ dataType, value = {}, onChange, choices }: {
  dataType: string;
  value?: Record<string, any>;
  onChange: (v: Record<string, any>) => void;
  choices?: Array<{ CodedValue: string; label: string }>;
}) {
  const v = (value || {}) as Record<string, any>;
  const set = (patch) => onChange({ ...v, ...patch });

  const isNumber = ['integer', 'float', 'currency', 'percent', 'feetinches'].includes(dataType);
  const isText = ['text', 'multilinetext'].includes(dataType);
  const isChoice = ['multiselect'].includes(dataType);
  const isDate = ['date', 'datetime'].includes(dataType);
  const isBoolean = dataType === 'boolean';
  const isChoiceType = dataType === 'singleselect' || dataType === 'multiselect';

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {isNumber && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <MTextField id="naomi-fieldtype-input-min-value" label="Min value" size="sm" type="number" value={v.min ?? ''} onChange={x => set({ min: x===''?undefined:Number(x) })} placeholder="—"/>
          <MTextField id="naomi-fieldtype-input-max-value" label="Max value" size="sm" type="number" value={v.max ?? ''} onChange={x => set({ max: x===''?undefined:Number(x) })} placeholder="—"/>
        </div>
      )}
      {isText && (
        <Fragment>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <MTextField id="naomi-fieldtype-input-min-length" label="Min length" size="sm" type="number" value={v.minLen ?? ''} onChange={x => set({ minLen: x===''?undefined:parseInt(x) })} placeholder="0"/>
            <MTextField id="naomi-fieldtype-input-max-length" label="Max length" size="sm" type="number" value={v.maxLen ?? ''} onChange={x => set({ maxLen: x===''?undefined:parseInt(x) })} placeholder="—"/>
          </div>
          <MSelect id="naomi-fieldtype-select-format-pattern" label="Format pattern" size="sm" value={v.pattern || ''}
            onChange={x => set({ pattern: x || undefined })}
            options={[
              { value: '', label: '— Any text —' },
              { value: 'email', label: 'Email address' },
              { value: 'phone', label: 'Phone (US)' },
              { value: 'zip',   label: 'ZIP code' },
              { value: 'url',   label: 'URL' },
              { value: 'custom',label: 'Custom regex…' },
            ]}/>
          {v.pattern === 'custom' && (
            <MTextField id="naomi-fieldtype-input-regex" label="Regex" size="sm" value={v.regex || ''} onChange={x => set({ regex: x })} placeholder="^[A-Z]{3}-\\d{4}$"/>
          )}
        </Fragment>
      )}
      {isChoice && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <MTextField id="naomi-fieldtype-input-min-selections" label="Min selections" size="sm" type="number" value={v.minSel ?? ''} onChange={x => set({ minSel: x===''?undefined:parseInt(x) })} placeholder="0"/>
          <MTextField id="naomi-fieldtype-input-max-selections" label="Max selections" size="sm" type="number" value={v.maxSel ?? ''} onChange={x => set({ maxSel: x===''?undefined:parseInt(x) })} placeholder="—"/>
        </div>
      )}
      {isDate && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <MTextField id="naomi-fieldtype-input-earliest-date" label="Earliest" size="sm" type="date" value={v.dateMin || ''} onChange={x => set({ dateMin: x || undefined })}/>
          <MTextField id="naomi-fieldtype-input-latest-date" label="Latest" size="sm" type="date" value={v.dateMax || ''} onChange={x => set({ dateMax: x || undefined })}/>
        </div>
      )}
      {(isBoolean || (isChoiceType && choices && choices.length > 0)) && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            fontSize: 11, fontWeight: 700, color: NT.warn, textTransform: 'uppercase', letterSpacing: .5,
          }}>
            <WarnIcon /> Warning messages
          </div>
          <ChoiceWarningEditor
            choices={isBoolean
              ? [{ CodedValue: 'Yes', label: 'Yes' }, { CodedValue: 'No', label: 'No' }]
              : (choices ?? [])}
            warnings={isBoolean
              ? { ...(v.warnYes ? { Yes: v.warnYes } : {}), ...(v.warnNo ? { No: v.warnNo } : {}) }
              : Object.fromEntries(
                  Object.keys(v)
                    .filter(k => k.startsWith('warnChoice_') && v[k])
                    .map(k => [k.slice('warnChoice_'.length), v[k]])
                )}
            onChange={w => {
              if (isBoolean) {
                set({ warnYes: w['Yes'] || undefined, warnNo: w['No'] || undefined });
              } else {
                const patch: Record<string, any> = {};
                Object.keys(v).filter(k => k.startsWith('warnChoice_')).forEach(k => { patch[k] = undefined; });
                Object.entries(w).forEach(([cv, msg]) => { if (msg) patch[`warnChoice_${cv}`] = msg; });
                set(patch);
              }
            }}
          />
        </div>
      )}
      <MTextField id="naomi-fieldtype-input-error-message"
        label={isBoolean ? 'Error message (Hard)' : 'Custom error message'} size="sm"
        value={v.message || ''}
        onChange={x => set({ message: x || undefined })}
        placeholder="Shown when the answer fails validation" />
    </div>
  );
}

export function rangeCheckToValidations(rangeCheck: any[]): Record<string, any> {
  if (!rangeCheck?.length) return {};
  const v: Record<string, any> = {};
  for (const rc of rangeCheck) {
    if (rc.SoftHard === 'Soft' && rc.Comparator === 'EQ') {
      const msg = rc.ErrorMessage?.TranslatedText?.text;
      if (!msg) continue;
      if (rc.CheckValue === 'Yes') v.warnYes = msg;
      else if (rc.CheckValue === 'No') v.warnNo = msg;
      else v[`warnChoice_${rc.CheckValue}`] = msg;
      continue;
    }
    const hardMsg = rc.ErrorMessage?.TranslatedText?.text;
    if (hardMsg && !v.message) v.message = hardMsg;
    const num = Number(rc.CheckValue);
    if (rc.Comparator === 'LE' || rc.Comparator === 'LT') v.min = num;
    if (rc.Comparator === 'GE' || rc.Comparator === 'GT') v.max = num;
  }
  return v;
}

export function validationsToRangeCheck(v: Record<string, any>): any[] {
  if (!v || !Object.keys(v).length) return [];
  const checks: any[] = [];
  const makeMsg = (text: string) => ({ TranslatedText: { lang: 'en', text } });
  const msg = v.message ? makeMsg(v.message) : undefined;
  if (v.min !== undefined) checks.push({ Comparator: 'LE', SoftHard: 'Hard', CheckValue: String(v.min), ...(msg ? { ErrorMessage: msg } : {}) });
  if (v.max !== undefined) checks.push({ Comparator: 'GE', SoftHard: 'Hard', CheckValue: String(v.max), ...(msg ? { ErrorMessage: msg } : {}) });
  if (v.warnYes) checks.push({ Comparator: 'EQ', SoftHard: 'Soft', CheckValue: 'Yes', ErrorMessage: makeMsg(v.warnYes) });
  if (v.warnNo) checks.push({ Comparator: 'EQ', SoftHard: 'Soft', CheckValue: 'No', ErrorMessage: makeMsg(v.warnNo) });
  for (const key of Object.keys(v)) {
    if (key.startsWith('warnChoice_') && v[key]) {
      checks.push({ Comparator: 'EQ', SoftHard: 'Soft', CheckValue: key.slice('warnChoice_'.length), ErrorMessage: makeMsg(v[key]) });
    }
  }
  return checks;
}

export function ValidationSummary({ validations, dataType }) {
  if (!validations) return null;
  const v = (Array.isArray(validations) ? rangeCheckToValidations(validations) : validations) as Record<string, any>;
  const parts: string[] = [];
  if (v.min !== undefined || v.max !== undefined) {
    if (v.min !== undefined && v.max !== undefined) parts.push(`${v.min}–${v.max}`);
    else if (v.min !== undefined) parts.push(`≥ ${v.min}`);
    else parts.push(`≤ ${v.max}`);
  }
  if (v.minLen !== undefined || v.maxLen !== undefined) {
    if (v.minLen && v.maxLen) parts.push(`${v.minLen}–${v.maxLen} chars`);
    else if (v.minLen) parts.push(`≥${v.minLen} chars`);
    else parts.push(`≤${v.maxLen} chars`);
  }
  if (v.pattern && v.pattern !== 'custom') parts.push(v.pattern);
  if (v.pattern === 'custom' && v.regex) parts.push('regex');
  if (v.minSel || v.maxSel) parts.push(`${v.minSel || 0}–${v.maxSel || '∞'} picks`);
  if (v.dateMin || v.dateMax) parts.push('date range');
  const warnLabels: string[] = [];
  if (v.warnYes) warnLabels.push('Yes');
  if (v.warnNo) warnLabels.push('No');
  const choiceWarnCount = Object.keys(v).filter(k => k.startsWith('warnChoice_') && v[k]).length;
  const totalWarnCount = warnLabels.length + choiceWarnCount;

  if (!parts.length && !totalWarnCount) return null;

  const warnSummary = choiceWarnCount > 0
    ? [...warnLabels, `${choiceWarnCount} option${choiceWarnCount > 1 ? 's' : ''}`].join('/')
    : warnLabels.join('/');
  return (
    <Fragment>
      {parts.length > 0 && (
        <NMChip color="info" icon={<NI.Validate />}>
          {parts.join(' · ')}
        </NMChip>
      )}
      {totalWarnCount > 0 && (
        <NMChip color="warn">
          ⚠ warns on {warnSummary}
        </NMChip>
      )}
    </Fragment>
  );
}
export function FtInPreview({ disabled = true }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <div style={{ flex: 1, height: 32, borderRadius: 4, border: `1px solid ${NT.line2}`, background: '#fff',
        display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: 12, color: NT.ink4 }}>0</div>
      <div style={{ height: 32, padding: '0 10px', borderRadius: 4, background: NT.surfaceAlt,
        display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 600, color: NT.ink3 }}>ft</div>
      <div style={{ flex: 1, height: 32, borderRadius: 4, border: `1px solid ${NT.line2}`, background: '#fff',
        display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: 12, color: NT.ink4 }}>0</div>
      <div style={{ height: 32, padding: '0 10px', borderRadius: 4, background: NT.surfaceAlt,
        display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 600, color: NT.ink3 }}>in</div>
    </div>
  );
}

export function FtInLiveInput({ value = { ft: '', in: '' }, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input className="nm-input" type="number" min="0" placeholder="0"
        value={value.ft ?? ''} onChange={e => onChange({ ...value, ft: e.target.value })}
        style={{ flex: 1 }}/>
      <span style={{ alignSelf: 'center', padding: '0 8px', color: NT.ink3, fontWeight: 600, fontSize: 13 }}>ft</span>
      <input className="nm-input" type="number" min="0" max="11" placeholder="0"
        value={value.in ?? ''} onChange={e => onChange({ ...value, in: e.target.value })}
        style={{ flex: 1 }}/>
      <span style={{ alignSelf: 'center', padding: '0 8px', color: NT.ink3, fontWeight: 600, fontSize: 13 }}>in</span>
    </div>
  );
}

export function UnitsManager({ open, onClose, sdm, onSave }) {
  const [draft, setDraft] = useState(sdm.BasicDefinition.MeasurementUnit);
  useEffect(() => { if (open) setDraft(sdm.BasicDefinition.MeasurementUnit); }, [open, sdm]);

  const builtinOids = ['MU.1','MU.2','MU.3','MU.4','MU.5'];
  const isBuiltin = oid => builtinOids.includes(oid);

  const update = (oid, patch) => setDraft(d => d.map(u => u.OID === oid ? { ...u, ...patch } : u));
  const remove = (oid) => setDraft(d => d.filter(u => u.OID !== oid));
  const add = () => {
    const n = (draft.length ? Math.max(...draft.map(u => parseInt((u.OID||'').replace(/\D/g,''))||0)) : 0) + 1;
    setDraft(d => [...d, { OID: `MU.U${n}`, Name: 'New unit', Symbol: 'unit', userAdded: true }]);
  };

  return (
    <MDialog id="naomi-fieldtype-dialog-units-manager" open={open} onClose={onClose} title="Measurement units" subtitle="Customize the unit catalog used across all questions"
      icon={<NI.Ruler/>} width={620}
      actions={<>
        <NMButton id="naomi-fieldtype-button-units-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
        <NMButton id="naomi-fieldtype-button-units-save" color="primary" icon={<NI.Check/>} onClick={() => { onSave(draft); onClose(); }}>Save catalog</NMButton>
      </>}>
      <div style={{ display: 'grid', gap: 8 }}>
        {/* Header row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 80px 30px', gap: 8,
          fontSize: 10.5, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .5,
          padding: '0 8px' }}>
          <span>Name</span><span>Symbol</span><span>Type</span><span></span>
        </div>
        {draft.map(u => (
          <div key={u.OID} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 80px 30px', gap: 8,
            alignItems: 'center', padding: '6px 8px', background: NT.surface, border: `1px solid ${NT.line}`, borderRadius: 6 }}>
            <input id={`naomi-fieldtype-input-unit-name-${u.OID}`} value={u.Name} onChange={e => update(u.OID, { Name: e.target.value })}
              disabled={isBuiltin(u.OID)}
              style={{ height: 30, border: `1px solid ${NT.line2}`, borderRadius: 4, padding: '0 8px',
                background: isBuiltin(u.OID) ? NT.surfaceAlt : '#fff', fontSize: 13, color: NT.ink, fontFamily: 'inherit' }}/>
            <input id={`naomi-fieldtype-input-unit-symbol-${u.OID}`} value={u.Symbol} onChange={e => update(u.OID, { Symbol: e.target.value })}
              disabled={isBuiltin(u.OID)}
              style={{ height: 30, border: `1px solid ${NT.line2}`, borderRadius: 4, padding: '0 8px',
                background: isBuiltin(u.OID) ? NT.surfaceAlt : '#fff', fontSize: 13, color: NT.ink,
                fontFamily: NT.fontMono, fontWeight: 600 }}/>
            <NMChip color={isBuiltin(u.OID) ? 'neutral' : 'success'}>{isBuiltin(u.OID) ? 'built-in' : 'custom'}</NMChip>
            <button id={`naomi-fieldtype-iconbutton-unit-remove-${u.OID}`} className="nm-icon-btn" disabled={isBuiltin(u.OID)} onClick={() => remove(u.OID)}
              title={isBuiltin(u.OID) ? "Built-in units can't be deleted" : 'Remove unit'}
              style={{ opacity: isBuiltin(u.OID) ? .3 : 1, cursor: isBuiltin(u.OID) ? 'not-allowed' : 'pointer' }}>
              <NI.Trash/>
            </button>
          </div>
        ))}
        <button id="naomi-fieldtype-button-unit-add" onClick={add} className="nm-dashed"
          style={{ width: '100%', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, color: NT.ink3, fontSize: 13, fontWeight: 500, marginTop: 4 }}>
          <NI.Plus/> Add custom unit
        </button>
      </div>
      <div style={{ marginTop: 14, padding: '10px 12px', background: NT.infoSoft, borderRadius: 6,
        fontSize: 12, color: NT.ink2, lineHeight: 1.45, border: `1px solid ${NT.info}22` }}>
        <strong style={{ color: NT.info }}>Tip:</strong> Custom units are available across every question and trade. Built-in units can't be removed but you can hide them by setting the per-question Unit to "None".
      </div>
    </MDialog>
  );
}

export function ForkPickerDialog({ open, onClose, onPick, trades: tradesProp, id }: {
  id?: string; open: boolean; onClose: () => void; onPick: (t: any) => void; trades?: any[];
}) {
  const trades: any[] = tradesProp ?? [];
  const [filter, setFilter] = useState('');
  const filtered = trades.filter(t => !filter ||
    t.trade.toLowerCase().includes(filter.toLowerCase()) ||
    t.category.toLowerCase().includes(filter.toLowerCase()));
  return (
    <MDialog id="naomi-fieldtype-dialog-fork-picker" open={open} onClose={onClose} title="Fork an existing template"
      subtitle="Start from a copy of any published trade — keep the questions, branching and calculations as your starting point"
      icon={<NI.Fork/>} width={680}
      actions={<>
        <NMButton id="naomi-fieldtype-button-fork-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
      </>}>
      <MTextField id="naomi-fieldtype-input-fork-search" placeholder="Search trades by name or category…" value={filter} onChange={setFilter}
        size="sm" adornment={<NI.Search/>}/>
      <div style={{ display: 'grid', gap: 6, marginTop: 12, maxHeight: 380, overflow: 'auto' }} className="naomi-scroll">
        {filtered.map(t => (
          <button key={t.trade} onClick={() => { onPick(t); onClose(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              background: NT.surface, border: `1px solid ${NT.line}`, borderRadius: 6,
              cursor: 'pointer', textAlign: 'left' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = NT.primary; e.currentTarget.style.background = NT.primarySoft + '60'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = NT.line; e.currentTarget.style.background = NT.surface; }}>
            <div style={{ width: 32, height: 32, borderRadius: 6, background: t.color, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              {t.trade.slice(0,1)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: NT.ink }}>{t.trade}</div>
              <div style={{ fontSize: 11.5, color: NT.ink3 }}>{t.category} · {t.items} questions · v{t.version}</div>
            </div>
            <NMChip color={t.status==='published'?'success':t.status==='draft'?'warn':'info'}>{t.status}</NMChip>
            <span style={{ color: NT.ink4 }}><NI.Fork/></span>
          </button>
        ))}
        {!filtered.length && <div style={{ padding: 24, textAlign: 'center', color: NT.ink4, fontSize: 13 }}>No trades match "{filter}"</div>}
      </div>
    </MDialog>
  );
}

export function newBlankSDM(name = 'Untitled trade template'): any {
  return {
    GlobalVariables: { Name: name, Description: 'Custom trade template', TemplateType: 'Standard' },
    BasicDefinition: {
      MeasurementUnit: [
        { OID: 'MU.1', Name: 'Square Feet', Symbol: 'SF', builtin: true },
        { OID: 'MU.2', Name: 'Linear Feet', Symbol: 'LF', builtin: true },
        { OID: 'MU.3', Name: 'Each',        Symbol: 'ea', builtin: true },
        { OID: 'MU.4', Name: 'Feet',        Symbol: 'ft', builtin: true },
        { OID: 'MU.5', Name: 'Inches',      Symbol: 'in', builtin: true },
      ],
    },
    TemplateVersion: [{
      version: '0.1',
      status: 'draft',
      Protocol: { EventRef: [{ EventOID: 'E.1' }] },
      EventDef: [{ OID: 'E.1', Name: 'Inspection', FormRef: [{ FormOID: 'F.1' }] }],
      FormDef: [{ OID: 'F.1', Name: name, ItemGroupRef: [{ ItemGroupOID: 'IG.1' }] }],
      ItemGroupDef: [{ OID: 'IG.1', Name: 'General', Repeating: 'No', ItemRef: [], Subgroups: [] }],
      ItemDef: [],
      CodeList: [],
      ConditionDef: [],
      MethodDef: [],
    }],
    history: [],
    draft: { version: '0.1', basedOn: null, status: 'draft', changedSince: [] },
  };
}

export function newAssetSDM(): any {
  return {
    GlobalVariables: {
      Name: 'Asset Questions',
      Description: 'Application-wide asset question template',
      TemplateType: 'Asset',
      tradeOid: 'A.1',
    },
    BasicDefinition: {
      MeasurementUnit: [
        { OID: 'MU.1', Name: 'Square Feet', Symbol: 'SF', builtin: true },
        { OID: 'MU.2', Name: 'Linear Feet', Symbol: 'LF', builtin: true },
        { OID: 'MU.3', Name: 'Each',        Symbol: 'ea', builtin: true },
        { OID: 'MU.4', Name: 'Feet',        Symbol: 'ft', builtin: true },
        { OID: 'MU.5', Name: 'Inches',      Symbol: 'in', builtin: true },
      ],
    },
    TemplateVersion: [{
      version: '0.1',
      status: 'draft',
      Protocol: { EventRef: [{ EventOID: 'E.1' }] },
      EventDef: [{ OID: 'E.1', Name: 'Asset', FormRef: [{ FormOID: 'F.1' }] }],
      FormDef: [{ OID: 'F.1', Name: 'Asset Questions', ItemGroupRef: [{ ItemGroupOID: 'IG.1' }] }],
      ItemGroupDef: [{ OID: 'IG.1', Name: 'Asset Details', Repeating: 'No', ItemRef: [], Subgroups: [] }],
      ItemDef: [],
      CodeList: [],
      ConditionDef: [],
      MethodDef: [],
    }],
    history: [],
    draft: { version: '0.1', basedOn: null, status: 'draft', changedSince: [] },
  };
}

export function forkSDM(sourceSDM, newName) {
  const clone = JSON.parse(JSON.stringify(sourceSDM));
  clone.GlobalVariables.Name = newName;
  clone.GlobalVariables.Description = `Forked from ${sourceSDM.GlobalVariables.Name}`;
  delete clone.GlobalVariables.tradeOid;
  delete clone.GlobalVariables.tradeName;
  clone.history = [];
  clone.draft = { version: '0.1', basedOn: `${sourceSDM.GlobalVariables.Name} v${sourceSDM.TemplateVersion[0].version}`, status: 'draft', changedSince: [] };
  clone.TemplateVersion[0].version = '0.1';
  clone.TemplateVersion[0].status = 'draft';
  return clone;
}

export function snapshotSDM(sdm, version) {
  const clone = JSON.parse(JSON.stringify(sdm));
  clone.TemplateVersion[0].version = version;
  clone.TemplateVersion[0].status = 'published';
  delete clone.draft;
  if (clone.TemplateVersion[0].CodeList) {
    clone.TemplateVersion[0].CodeList = clone.TemplateVersion[0].CodeList.map(cl => ({
      ...cl,
      CodeListItem: (cl.CodeListItem || []).map(({ _previewUrl: _p, IconUrl: _i, ...rest }) => rest),
    }));
  }
  return clone;
}

Object.assign(window, {
  DataTypeOptions2, TypeMeta2, DataTypePicker, ValidationEditor, ValidationSummary,
  FtInPreview, FtInLiveInput, UnitsManager, ForkPickerDialog,
  newBlankSDM, newAssetSDM, forkSDM, snapshotSDM, unitsFor,
});
